import { useMemo } from 'react';
import { useSanityInsights } from '@jwp/ott-hooks-react/src/cms/useSanityInsights';
import type { Category, Insight, SidebarArticle, TransformedData } from '@jwp/ott-common/types/insights';
import { formatDistanceToNow } from 'date-fns';

import { buildSanityImageUrl } from './cms/utils/imageUtils';

export const useInsightsData = (): {
  data: TransformedData;
  isLoading: boolean;
} => {
  const { data: contentData, isLoading } = useSanityInsights();

  const transformedData = useMemo((): TransformedData => {
    if (!contentData || !Array.isArray(contentData)) {
      return { insights: [], sidebarArticles: [], categories: [] };
    }

    const insights: Insight[] = [];
    const sidebarArticles: SidebarArticle[] = [];
    const categorySet = new Set<Category>();

    contentData.forEach((item: any, index: number) => {
      const firstTextBlock = item.content?.find((block: any) => block._type === 'block' && block.children?.[0]?.text);
      const description = firstTextBlock?.children?.[0]?.text || '';

      const meta = `${formatDistanceToNow(item.publishDate)} • ${item.author?.name || 'Corrigan Sports Network Staff'}`;
      const imageUrl = item.thumbnail?.asset?._ref ? buildSanityImageUrl(item.thumbnail.asset._ref) : undefined;
      const category = item.category?.title || 'General';

      const transformedInsight: Insight = {
        id: item.slug || `insight-${index}`,
        title: item.title,
        description: description.substring(0, 150) + (description.length > 150 ? '...' : ''),
        image: imageUrl,
        meta,
        category,
        fullContent: item.content || [],
        publishDate: item.publishDate,
        author: item.author?.name || 'Corrigan Sports Network Staff',
        thumbnailSource: item.thumbnailSource,
        slug: item.slug,
      };

      insights.push(transformedInsight);
      categorySet.add(category);

      sidebarArticles.push({
        id: transformedInsight.id,
        title: transformedInsight.title,
        meta: transformedInsight.meta,
        category: transformedInsight.category,
      });
    });

    return {
      insights,
      sidebarArticles,
      categories: Array.from(categorySet).sort(),
    };
  }, [contentData]);

  return {
    data: transformedData,
    isLoading,
  };
};
